import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';

const POLL_INTERVAL_MS = 2500;

const EMPTY_FORM = {
  name: '',
  subject: '',
  content: '',
  sender_name: '',
  sender_email: '',
  prospect_list_id: '',
  schedule_type: 'immediate',
  follow_up_enabled: false,
  follow_up_days: 3,
  follow_up_subject: '',
  follow_up_content: '',
  track_opens: true,
  track_clicks: true,
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso; }
};

const LOW_ENGAGEMENT_COLOR = '#f97316';
const DEFAULT_ENGAGEMENT_COLOR = '#10b981';

const CustomChartTooltip = ({ active, payload, label, lowEngagementThreshold = 10 }) => {
  if (!active || !payload || !payload.length) return null;
  const sentCount = payload[0]?.payload?.sent_count ?? null;
  const displayName = payload[0]?.payload?.fullName || label?.split('\n')[0] || label;
  const openRate = payload.find(e => e.dataKey === 'Aberturas (%)')?.value ?? null;
  const isLowEngagement = openRate !== null && openRate < lowEngagementThreshold;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-slate-800 mb-1 truncate max-w-[160px]">{displayName}</p>
      {isLowEngagement && (
        <div className="flex items-center gap-1 mb-1.5 px-1.5 py-0.5 bg-orange-50 border border-orange-200 rounded text-orange-600 font-medium">
          <span>⚠️</span>
          <span>Engajamento baixo</span>
        </div>
      )}
      {sentCount !== null && (
        <div className="flex items-center gap-1.5 mb-1 pb-1 border-b border-slate-100">
          <span className="text-slate-500">📧 Enviados:</span>
          <span className="font-semibold text-slate-800">{sentCount.toLocaleString('pt-BR')}</span>
        </div>
      )}
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
          <span className="text-slate-600">{entry.name}:</span>
          <span className="font-medium text-slate-900">{entry.value}%</span>
        </div>
      ))}
    </div>
  );
};

const CampaignDateTick = ({ x, y, payload }) => {
  const parts = (payload.value || '').split('\n');
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={12} textAnchor="middle" fill="#64748b" fontSize={11}>
        {parts[0]}
      </text>
      {parts[1] && (
        <text x={0} y={0} dy={24} textAnchor="middle" fill="#94a3b8" fontSize={10}>
          {parts[1]}
        </text>
      )}
    </g>
  );
};

const SORT_OPTIONS = [
  { value: 'date', label: 'Data' },
  { value: 'open_rate', label: 'Abertura' },
  { value: 'click_rate', label: 'Cliques' },
  { value: 'reply_rate', label: 'Respostas' },
  { value: 'alpha', label: 'A–Z' },
];

const THRESHOLD_OPTIONS = [5, 10, 20, 30];

const CampaignPerformanceChart = ({ campaigns, onSelectCampaign }) => {
  const [sortOrder, setSortOrder] = useState('date');
  const [engagementThreshold, setEngagementThreshold] = useState(10);

  const baseCampaigns = campaigns.filter(c => (c.sent_count ?? 0) > 0).slice();

  const sentCampaigns = baseCampaigns.sort((a, b) => {
    if (sortOrder === 'date') {
      return new Date(a.schedule_date || a.created_at) - new Date(b.schedule_date || b.created_at);
    }
    if (sortOrder === 'open_rate') return parseFloat(b.open_rate ?? 0) - parseFloat(a.open_rate ?? 0);
    if (sortOrder === 'click_rate') return parseFloat(b.click_rate ?? 0) - parseFloat(a.click_rate ?? 0);
    if (sortOrder === 'reply_rate') return parseFloat(b.reply_rate ?? 0) - parseFloat(a.reply_rate ?? 0);
    if (sortOrder === 'alpha') return a.name.localeCompare(b.name, 'pt-BR');
    return 0;
  });

  if (sentCampaigns.length === 0) return null;

  const data = sentCampaigns.map(c => {
    const dateObj = new Date(c.schedule_date || c.created_at);
    const dateLabel = isNaN(dateObj) ? '' : dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    const shortName = c.name.length > 18 ? c.name.slice(0, 16) + '…' : c.name;
    return {
      id: c.id,
      name: dateLabel ? `${shortName}\n${dateLabel}` : shortName,
      fullName: c.name,
      sent_count: c.sent_count ?? 0,
      'Aberturas (%)': parseFloat(c.open_rate ?? 0),
      'Cliques (%)': parseFloat(c.click_rate ?? 0),
      'Respostas (%)': parseFloat(c.reply_rate ?? 0),
    };
  });

  const handleBarClick = (barData) => {
    if (barData && barData.activePayload && barData.activePayload[0]) {
      const id = barData.activePayload[0].payload.id;
      if (id) onSelectCampaign(id);
    }
  };

  const maxSent = Math.max(...data.map(d => d.sent_count), 1);

  return (
    <Card className="glass border-white/20 shadow-xl">
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="text-xl text-slate-800">📈 Desempenho das Campanhas</CardTitle>
            <CardDescription>Clique em uma barra para ver detalhes da campanha</CardDescription>
          </div>
          <div className="flex flex-col gap-1.5 items-end">
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <span className="text-xs text-slate-500 mr-1">Ordenar:</span>
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSortOrder(opt.value)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors border ${
                    sortOrder === opt.value
                      ? 'bg-slate-800 text-white border-slate-800'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-800'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <span className="text-xs text-orange-500 mr-1">⚠️ Alerta de abertura abaixo de:</span>
              {THRESHOLD_OPTIONS.map(t => (
                <button
                  key={t}
                  onClick={() => setEngagementThreshold(t)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors border ${
                    engagementThreshold === t
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300 hover:text-orange-600'
                  }`}
                >
                  {t}%
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={data}
            margin={{ top: 8, right: 56, left: 0, bottom: 24 }}
            onClick={handleBarClick}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="name"
              tick={<CampaignDateTick />}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
              interval={0}
            />
            <YAxis
              yAxisId="left"
              tickFormatter={v => `${v}%`}
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={false}
              domain={[0, 100]}
              width={40}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : `${v}`}
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              tickLine={false}
              axisLine={false}
              domain={[0, maxSent * 1.15]}
              width={44}
              label={{ value: 'Enviados', angle: 90, position: 'insideRight', offset: 12, style: { fontSize: 10, fill: '#94a3b8' } }}
            />
            <Tooltip content={<CustomChartTooltip lowEngagementThreshold={engagementThreshold} />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
            />
            <Bar
              yAxisId="right"
              dataKey="sent_count"
              name="Enviados (vol.)"
              fill="#94a3b8"
              fillOpacity={0.18}
              radius={[3, 3, 0, 0]}
              maxBarSize={48}
            />
            <Bar yAxisId="left" dataKey="Aberturas (%)" fill={DEFAULT_ENGAGEMENT_COLOR} radius={[4, 4, 0, 0]} maxBarSize={40}>
              {data.map((entry, index) => (
                <Cell
                  key={`cell-open-${index}`}
                  fill={entry['Aberturas (%)'] < engagementThreshold ? LOW_ENGAGEMENT_COLOR : DEFAULT_ENGAGEMENT_COLOR}
                />
              ))}
            </Bar>
            <Bar yAxisId="left" dataKey="Cliques (%)" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={40} />
            <Bar yAxisId="left" dataKey="Respostas (%)" fill="#a855f7" radius={[4, 4, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

const TrackingModal = ({ campaign, onClose, api }) => {
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyFilter, setReplyFilter] = useState('all');

  useEffect(() => {
    if (!campaign) return;
    setLoading(true);
    api.get(`/prospecting/campaigns/${campaign.id}/tracking`)
      .then(r => { setRecords(r.data.records || []); })
      .catch(e => setError(e?.response?.data?.detail || 'Erro ao carregar rastreamento'))
      .finally(() => setLoading(false));
  }, [campaign, api]);

  if (!campaign) return null;

  const opens = records ? records.filter(r => r.event === 'open') : [];
  const clicks = records ? records.filter(r => r.event === 'click') : [];

  const openByProspect = {};
  opens.forEach(r => { openByProspect[r.prospect_id] = r; });
  const clickByProspect = {};
  clicks.forEach(r => { clickByProspect[r.prospect_id] = r; });

  const repliedByProspect = {};
  if (records) {
    records.forEach(r => {
      if (r.replied) repliedByProspect[r.prospect_id] = r;
    });
  }

  const allProspectIds = [...new Set([
    ...Object.keys(openByProspect),
    ...Object.keys(clickByProspect),
    ...Object.keys(repliedByProspect),
  ])];

  const repliedCount = Object.keys(repliedByProspect).length;

  const filteredProspectIds = allProspectIds.filter(pid => {
    if (replyFilter === 'replied') return !!repliedByProspect[pid];
    if (replyFilter === 'not_replied') return !repliedByProspect[pid];
    return true;
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>📊 Histórico de Rastreamento</DialogTitle>
          <DialogDescription>
            Aberturas, cliques e respostas por prospecto — <span className="font-medium text-slate-700">{campaign.name}</span>
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-slate-400 text-sm animate-pulse">Carregando dados de rastreamento…</div>
        )}

        {error && (
          <div className="py-6 text-center text-red-500 text-sm">{error}</div>
        )}

        {!loading && !error && records !== null && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-700">{opens.length}</p>
                <p className="text-xs text-blue-600 mt-0.5">Prospectos que abriram</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-purple-700">{clicks.length}</p>
                <p className="text-xs text-purple-600 mt-0.5">Prospectos que clicaram</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{repliedCount}</p>
                <p className="text-xs text-green-600 mt-0.5">Prospectos que responderam</p>
              </div>
            </div>

            {allProspectIds.length > 0 && (
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-slate-500 font-medium">Filtrar:</span>
                {[
                  { value: 'all', label: 'Todos' },
                  { value: 'replied', label: '✉ Responderam' },
                  { value: 'not_replied', label: 'Não responderam' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setReplyFilter(opt.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      replyFilter === opt.value
                        ? 'bg-green-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {allProspectIds.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                Nenhum evento de abertura, clique ou resposta registrado ainda.
              </div>
            ) : filteredProspectIds.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                Nenhum prospecto encontrado para este filtro.
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Prospecto</th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Email</th>
                      <th className="text-center py-2 px-3 font-semibold text-blue-600">👁 Abertura</th>
                      <th className="text-center py-2 px-3 font-semibold text-purple-600">🖱 Clique</th>
                      <th className="text-center py-2 px-3 font-semibold text-green-600">✉ Respondeu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProspectIds.map((pid, i) => {
                      const op = openByProspect[pid];
                      const cl = clickByProspect[pid];
                      const re = repliedByProspect[pid];
                      const name = (op || cl || re)?.name || '—';
                      const email = (op || cl || re)?.email || '—';
                      return (
                        <tr key={pid} className={`border-t border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                          <td className="py-2 px-3 text-slate-800 font-medium">{name}</td>
                          <td className="py-2 px-3 text-slate-500">{email}</td>
                          <td className="py-2 px-3 text-center">
                            {op ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="inline-flex items-center gap-1 text-blue-700 font-semibold">
                                  ✓ <span>{op.count}×</span>
                                </span>
                                <span className="text-slate-400 text-[10px]">1ª vez: {fmtDate(op.first_seen)}</span>
                                {op.count > 1 && (
                                  <span className="text-slate-400 text-[10px]">última: {fmtDate(op.last_seen)}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-200">—</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {cl ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="inline-flex items-center gap-1 text-purple-700 font-semibold">
                                  ✓ <span>{cl.count}×</span>
                                </span>
                                <span className="text-slate-400 text-[10px]">1ª vez: {fmtDate(cl.first_seen)}</span>
                                {cl.count > 1 && (
                                  <span className="text-slate-400 text-[10px]">última: {fmtDate(cl.last_seen)}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-200">—</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {re ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="inline-flex items-center gap-1 text-green-700 font-semibold">
                                  ✓ Sim
                                </span>
                                {re.replied_at && (
                                  <span className="text-slate-400 text-[10px]">{fmtDate(re.replied_at)}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const REPLIES_REFRESH_MS = 30000;

const DATE_FILTER_OPTIONS = [
  { value: '', label: 'Qualquer data' },
  { value: 'today', label: 'Hoje' },
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
];

function getDateBounds(filter) {
  if (!filter) return {};
  const now = new Date();
  let from;
  if (filter === 'today') {
    from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  } else if (filter === '7d') {
    from = new Date(now - 7 * 24 * 60 * 60 * 1000);
  } else if (filter === '30d') {
    from = new Date(now - 30 * 24 * 60 * 60 * 1000);
  }
  return { date_from: from ? from.toISOString() : undefined };
}

export default function EmailMarketing({ api }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [sendStatus, setSendStatus] = useState({});
  const pollRef = useRef({});
  const initialFetchedRef = useRef(new Set());

  const [activeView, setActiveView] = useState('campaigns');

  const [trackingCampaign, setTrackingCampaign] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [prospectLists, setProspectLists] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [previewTab, setPreviewTab] = useState({});

  const [repliedProspects, setRepliedProspects] = useState([]);
  const [repliedLoading, setRepliedLoading] = useState(false);
  const [repliedCampaignFilter, setRepliedCampaignFilter] = useState('');
  const [repliedDateFilter, setRepliedDateFilter] = useState('');
  const repliesIntervalRef = useRef(null);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await api.get('/prospecting/campaigns');
      setCampaigns(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar campanhas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchProspectLists = useCallback(async () => {
    try {
      const res = await api.get('/prospecting/lists');
      setProspectLists(res.data || []);
    } catch (err) {
      console.error('Error fetching prospect lists:', err);
    }
  }, [api]);

  const fetchRepliedProspects = useCallback(async (campaignFilter, dateFilter) => {
    setRepliedLoading(true);
    try {
      const params = {};
      if (campaignFilter) params.campaign_id = campaignFilter;
      const bounds = getDateBounds(dateFilter);
      if (bounds.date_from) params.date_from = bounds.date_from;
      const res = await api.get('/prospecting/replied-prospects', { params });
      setRepliedProspects(res.data?.replied_prospects || []);
    } catch (err) {
      console.error('Error fetching replied prospects:', err);
    } finally {
      setRepliedLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchCampaigns();
    fetchProspectLists();
  }, [fetchCampaigns, fetchProspectLists]);

  const pollStatus = useCallback(async (campaignId) => {
    try {
      const res = await api.get(`/prospecting/campaigns/${campaignId}/status`);
      const data = res.data;
      setSendStatus(prev => ({ ...prev, [campaignId]: data }));

      if (data.status === 'sending') {
        pollRef.current[campaignId] = setTimeout(() => pollStatus(campaignId), POLL_INTERVAL_MS);
      } else {
        delete pollRef.current[campaignId];
        fetchCampaigns();
      }
    } catch (err) {
      console.error('Status poll error:', err);
      delete pollRef.current[campaignId];
    }
  }, [api, fetchCampaigns]);

  useEffect(() => {
    campaigns.forEach(c => {
      if (c.status === 'sending' && !pollRef.current[c.id]) {
        pollRef.current[c.id] = setTimeout(() => pollStatus(c.id), POLL_INTERVAL_MS);
      } else if (
        c.status !== 'draft' &&
        c.status !== 'sending' &&
        !initialFetchedRef.current.has(c.id)
      ) {
        initialFetchedRef.current.add(c.id);
        api.get(`/prospecting/campaigns/${c.id}/status`)
          .then(res => setSendStatus(prev => ({ ...prev, [c.id]: res.data })))
          .catch(err => console.error('Error pre-loading campaign status:', err));
      }
    });
  }, [campaigns, pollStatus, api]);

  useEffect(() => {
    return () => {
      Object.values(pollRef.current).forEach(t => clearTimeout(t));
    };
  }, []);

  useEffect(() => {
    fetchRepliedProspects(repliedCampaignFilter, repliedDateFilter);
    clearInterval(repliesIntervalRef.current);
    repliesIntervalRef.current = setInterval(
      () => fetchRepliedProspects(repliedCampaignFilter, repliedDateFilter),
      REPLIES_REFRESH_MS
    );
    return () => clearInterval(repliesIntervalRef.current);
  }, [fetchRepliedProspects, repliedCampaignFilter, repliedDateFilter]);

  const handleLaunch = async (campaignId) => {
    try {
      const res = await api.post(`/prospecting/campaigns/${campaignId}/launch`);
      toast.success('Envio iniciado!');
      setCampaigns(prev =>
        prev.map(c => c.id === campaignId ? { ...c, status: 'sending', sent_count: 0, failed_count: 0, total_prospects: res.data.total_prospects } : c)
      );
      setSendStatus(prev => ({
        ...prev,
        [campaignId]: {
          status: 'sending',
          sent_count: 0,
          failed_count: 0,
          total_prospects: res.data.total_prospects,
          processed_count: 0,
          progress_pct: 0,
          deliveries: [],
        }
      }));
      pollRef.current[campaignId] = setTimeout(() => pollStatus(campaignId), POLL_INTERVAL_MS);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao iniciar campanha');
    }
  };

  const handleCancel = async (campaignId) => {
    try {
      await api.post(`/prospecting/campaigns/${campaignId}/cancel`);
      toast.success('Cancelamento solicitado — o envio será interrompido em breve');
      setCampaigns(prev =>
        prev.map(c => c.id === campaignId ? { ...c, status: 'cancelled' } : c)
      );
      clearTimeout(pollRef.current[campaignId]);
      delete pollRef.current[campaignId];
      fetchCampaigns();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao cancelar campanha');
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.content.trim() || !form.sender_name.trim() || !form.sender_email.trim() || !form.prospect_list_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        follow_up_subject: form.follow_up_enabled && form.follow_up_subject.trim() ? form.follow_up_subject.trim() : null,
        follow_up_content: form.follow_up_enabled && form.follow_up_content.trim() ? form.follow_up_content.trim() : null,
      };
      await api.post('/prospecting/campaigns', payload);
      toast.success('Campanha criada com sucesso!');
      setShowCreateModal(false);
      setForm(EMPTY_FORM);
      fetchCampaigns();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao criar campanha');
    } finally {
      setCreating(false);
    }
  };

  const openCreateModal = () => {
    setForm(EMPTY_FORM);
    setShowCreateModal(true);
  };

  const openEditModal = (campaign) => {
    setEditingCampaign(campaign);
    setEditForm({
      name: campaign.name || '',
      subject: campaign.subject || '',
      content: campaign.content || '',
      sender_name: campaign.sender_name || '',
      sender_email: campaign.sender_email || '',
      prospect_list_id: campaign.prospect_list_id || '',
      schedule_type: campaign.schedule_type || 'immediate',
      follow_up_enabled: campaign.follow_up_enabled || false,
      follow_up_days: campaign.follow_up_days || 3,
      follow_up_subject: campaign.follow_up_subject || '',
      follow_up_content: campaign.follow_up_content || '',
      track_opens: campaign.track_opens !== false,
      track_clicks: campaign.track_clicks !== false,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editForm.name.trim() || !editForm.subject.trim() || !editForm.content.trim() || !editForm.sender_name.trim() || !editForm.sender_email.trim() || !editForm.prospect_list_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        follow_up_subject: editForm.follow_up_enabled && editForm.follow_up_subject.trim() ? editForm.follow_up_subject.trim() : null,
        follow_up_content: editForm.follow_up_enabled && editForm.follow_up_content.trim() ? editForm.follow_up_content.trim() : null,
      };
      await api.put(`/prospecting/campaigns/${editingCampaign.id}`, payload);
      toast.success('Campanha atualizada com sucesso!');
      setShowEditModal(false);
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao atualizar campanha');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sending':   return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'active':    return 'bg-green-100 text-green-800 border-green-200';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed_with_errors': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'draft':     return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'scheduled': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'paused':    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'failed':    return 'bg-red-100 text-red-800 border-red-200';
      default:          return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'sending':   return 'Enviando…';
      case 'active':    return 'Ativa';
      case 'completed': return 'Finalizada';
      case 'completed_with_errors': return 'Concluída c/ erros';
      case 'draft':     return 'Rascunho';
      case 'scheduled': return 'Agendada';
      case 'paused':    return 'Pausada';
      case 'cancelled': return 'Cancelada';
      case 'failed':    return 'Falhou';
      default:          return status;
    }
  };

  const getFollowUpBadge = (campaign) => {
    const status = campaign.follow_up_status;
    if (!status || status === 'none') return null;
    const skipped = campaign.follow_up_skipped_count || 0;
    const skippedNote = skipped > 0 ? `, ${skipped} já engajados` : '';
    const map = {
      scheduled: { label: `Follow-up agendado (${campaign.follow_up_days || 3}d)`, color: 'bg-blue-100 text-blue-700 border-blue-200' },
      sending:   { label: 'Enviando follow-up…', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
      sent:      { label: `Follow-up enviado (${campaign.follow_up_sent_count || 0}${skippedNote})`, color: 'bg-green-100 text-green-700 border-green-200' },
      sent_with_errors: { label: `Follow-up c/ erros (${campaign.follow_up_sent_count || 0}/${(campaign.follow_up_sent_count || 0) + (campaign.follow_up_failed_count || 0)}${skippedNote})`, color: 'bg-orange-100 text-orange-700 border-orange-200' },
      failed:    { label: 'Follow-up falhou', color: 'bg-red-100 text-red-700 border-red-200' },
    };
    const s = map[status];
    if (!s) return null;
    return (
      <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border ${s.color}`}>
        🔄 {s.label}
      </span>
    );
  };

  const DeliveryStatusDot = ({ status }) => {
    const colors = { sent: 'bg-green-500', failed: 'bg-red-500', pending: 'bg-yellow-400' };
    return <span className={`inline-block w-2 h-2 rounded-full ${colors[status] || 'bg-gray-400'}`} />;
  };

  const CampaignProgressBar = ({ campaignId, campaign }) => {
    const st = sendStatus[campaignId];
    const isSending = campaign.status === 'sending' || st?.status === 'sending';
    if (!isSending && !st) return null;

    const total = st?.total_prospects || campaign.total_prospects || 0;
    const sent = st?.sent_count ?? campaign.sent_count ?? 0;
    const failed = st?.failed_count ?? campaign.failed_count ?? 0;
    const processed = sent + failed;
    const pct = total > 0 ? Math.round((processed / total) * 100) : 0;

    return (
      <div className="mt-3">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>Enviando… {processed}/{total}</span>
          <span>{pct}%</span>
        </div>
        <Progress value={pct} className="h-2" />
        <div className="flex space-x-4 mt-1 text-xs">
          <span className="text-green-600">✓ {sent} enviados</span>
          {failed > 0 && <span className="text-red-500">✗ {failed} falhou</span>}
        </div>
      </div>
    );
  };

  const [replyingId, setReplyingId] = useState(null);

  const handleMarkReplied = async (campaignId, prospectId) => {
    const key = `${campaignId}:${prospectId}`;
    setReplyingId(key);
    try {
      await api.post(`/prospecting/campaigns/${campaignId}/deliveries/${prospectId}/mark-replied`);
      toast.success('Prospecto marcado como respondeu — não receberá mais follow-ups.');
      setSendStatus(prev => {
        const st = prev[campaignId];
        if (!st) return prev;
        return {
          ...prev,
          [campaignId]: {
            ...st,
            deliveries: st.deliveries.map(d =>
              d.prospect_id === prospectId ? { ...d, replied: true } : d
            ),
          },
        };
      });
      setCampaigns(prev =>
        prev.map(c => {
          if (c.id !== campaignId) return c;
          const newReplyCount = (c.reply_count || 0) + 1;
          const sentCount = c.sent_count || 0;
          const newReplyRate = sentCount > 0 ? Math.round((newReplyCount / sentCount) * 10000) / 100 : 0;
          return { ...c, reply_count: newReplyCount, reply_rate: newReplyRate };
        })
      );
    } catch (err) {
      toast.error('Erro ao marcar como respondeu');
      console.error(err);
    } finally {
      setReplyingId(null);
    }
  };

  const DeliveryTable = ({ campaignId }) => {
    const st = sendStatus[campaignId];
    if (!st || !st.deliveries || st.deliveries.length === 0) return null;

    return (
      <div className="mt-4">
        <h4 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Entrega por prospecto</h4>
        <div className="max-h-64 overflow-y-auto rounded border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Prospecto</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Email</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                <th className="text-center py-2 px-3 font-medium text-slate-600">Engaj.</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Detalhe</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {st.deliveries.map((d, i) => {
                const loadingKey = `${campaignId}:${d.prospect_id}`;
                const isReplying = replyingId === loadingKey;
                return (
                  <tr key={i} className={`border-t border-slate-100 hover:bg-slate-50 ${d.replied ? 'bg-green-50/40' : ''}`}>
                    <td className="py-1.5 px-3 text-slate-700">{d.name || '—'}</td>
                    <td className="py-1.5 px-3 text-slate-500">{d.email}</td>
                    <td className="py-1.5 px-3">
                      <span className="flex items-center gap-1">
                        <DeliveryStatusDot status={d.status} />
                        <span className={d.status === 'sent' ? 'text-green-700' : 'text-red-600'}>
                          {d.status === 'sent' ? 'Enviado' : 'Falhou'}
                        </span>
                      </span>
                    </td>
                    <td className="py-1.5 px-3 text-center">
                      <span className="inline-flex items-center gap-1.5">
                        <span title={d.opened ? `Abriu${d.opened_at ? ': ' + new Date(d.opened_at).toLocaleString('pt-BR') : ''}` : 'Não abriu'}
                          className={d.opened ? 'text-blue-500' : 'text-slate-200'}>👁</span>
                        <span title={d.clicked ? `Clicou${d.clicked_at ? ': ' + new Date(d.clicked_at).toLocaleString('pt-BR') : ''}` : 'Não clicou'}
                          className={d.clicked ? 'text-purple-500' : 'text-slate-200'}>🖱</span>
                      </span>
                    </td>
                    <td className="py-1.5 px-3 text-slate-400">{d.error || (d.sent_at ? new Date(d.sent_at).toLocaleTimeString('pt-BR') : '—')}</td>
                    <td className="py-1.5 px-3 text-right">
                      {d.replied ? (
                        <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                          ✓ Respondeu
                        </span>
                      ) : d.prospect_id ? (
                        <button
                          onClick={() => handleMarkReplied(campaignId, d.prospect_id)}
                          disabled={isReplying}
                          className="text-slate-400 hover:text-blue-600 disabled:opacity-50 transition-colors whitespace-nowrap"
                          title="Marcar como respondeu — não receberá follow-ups"
                        >
                          {isReplying ? '…' : 'Marcar respondeu'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-slate-200 rounded w-1/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="email-marketing">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">📧 Email Marketing</h1>
          <p className="text-slate-600 mt-1">Gerencie campanhas e comunicação com leads</p>
        </div>
        <div className="flex items-center space-x-2">
          {activeView === 'campaigns' && (
            <>
              <Button onClick={fetchCampaigns} variant="outline" size="sm">
                ↻ Atualizar
              </Button>
              <Button onClick={openCreateModal} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                + Nova Campanha
              </Button>
            </>
          )}
          {activeView === 'replies' && (
            <Button onClick={() => fetchRepliedProspects(repliedCampaignFilter, repliedDateFilter)} variant="outline" size="sm" disabled={repliedLoading}>
              {repliedLoading ? '…' : '↻ Atualizar'}
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200 pb-0">
        <button
          onClick={() => setActiveView('campaigns')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeView === 'campaigns'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          📋 Campanhas
        </button>
        <button
          onClick={() => setActiveView('replies')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-2 ${
            activeView === 'replies'
              ? 'border-purple-600 text-purple-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          ✉️ Responderam
          {repliedProspects.length > 0 && (
            <span className={`inline-flex items-center justify-center rounded-full text-[11px] font-bold px-1.5 py-0.5 min-w-[20px] ${
              activeView === 'replies' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700'
            }`}>
              {repliedProspects.length}
            </span>
          )}
        </button>
      </div>

      {activeView === 'replies' && (
        <Card className="glass border-white/20 shadow-xl">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="text-xl text-slate-800">✉️ Prospects que Responderam</CardTitle>
                <CardDescription>Feed em tempo real de respostas — atualiza a cada 30 s</CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={repliedCampaignFilter}
                  onChange={e => setRepliedCampaignFilter(e.target.value)}
                  className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  <option value="">Todas as campanhas</option>
                  {campaigns.filter(c => c.reply_count > 0 || !repliedCampaignFilter || c.id === repliedCampaignFilter).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <select
                  value={repliedDateFilter}
                  onChange={e => setRepliedDateFilter(e.target.value)}
                  className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  {DATE_FILTER_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {repliedLoading && repliedProspects.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">Carregando…</div>
            ) : repliedProspects.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-4xl mb-3">📬</p>
                <p className="text-slate-500 font-medium">Nenhuma resposta encontrada</p>
                <p className="text-slate-400 text-sm mt-1">
                  {repliedCampaignFilter || repliedDateFilter
                    ? 'Tente remover os filtros para ver todas as respostas.'
                    : 'Quando um prospect responder ao e-mail, ele aparecerá aqui.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {repliedProspects.map((p, i) => (
                  <div
                    key={`${p.prospect_id}-${p.campaign_id}-${i}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-purple-50/60 border border-purple-100 hover:bg-purple-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-sm flex-shrink-0">
                        {(p.name || p.email || '?')[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">{p.name || p.email}</p>
                        {p.name && <p className="text-xs text-slate-500 truncate">{p.email}</p>}
                        <p className="text-xs text-purple-600 truncate">📋 {p.campaign_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                      <div className="flex gap-1">
                        {p.opened && (
                          <span title="Abriu o email" className="text-[10px] bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5 font-medium">👀 Abriu</span>
                        )}
                        {p.clicked && (
                          <span title="Clicou em um link" className="text-[10px] bg-blue-100 text-blue-700 rounded px-1.5 py-0.5 font-medium">🔗 Clicou</span>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-[11px] text-slate-400 whitespace-nowrap">{fmtDate(p.replied_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-slate-400 text-center pt-1">
                  {repliedProspects.length} resposta{repliedProspects.length !== 1 ? 's' : ''} encontrada{repliedProspects.length !== 1 ? 's' : ''}
                  {(repliedCampaignFilter || repliedDateFilter) && ' (com filtros aplicados)'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeView === 'campaigns' && (
      <>
      <CampaignPerformanceChart
        campaigns={campaigns}
        onSelectCampaign={(id) => setSelectedCampaign(prev => prev === id ? null : id)}
      />

      <Card className="glass border-white/20 shadow-xl">
        <CardHeader>
          <CardTitle className="text-xl text-slate-800">📋 Campanhas</CardTitle>
          <CardDescription>Campanhas de email marketing e status de entrega</CardDescription>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-slate-500 mb-4">Nenhuma campanha encontrada.</p>
              <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700 text-white">
                + Criar primeira campanha
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {campaigns.map((campaign) => {
                const isExpanded = selectedCampaign === campaign.id;
                const isSending = campaign.status === 'sending' || sendStatus[campaign.id]?.status === 'sending';
                const st = sendStatus[campaign.id];
                const displaySent = st?.sent_count ?? campaign.sent_count ?? 0;
                const displayFailed = st?.failed_count ?? campaign.failed_count ?? 0;

                return (
                  <div key={campaign.id} className="rounded-lg bg-white/50 border overflow-hidden">
                    <div className="flex items-start justify-between p-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-1 flex-wrap gap-y-1">
                          <h3 className="font-semibold text-slate-900">{campaign.name}</h3>
                          <Badge className={getStatusColor(campaign.status)}>
                            {isSending && <span className="mr-1 animate-pulse">●</span>}
                            {getStatusLabel(campaign.status)}
                          </Badge>
                        </div>

                        <p className="text-sm text-slate-600 mb-2 truncate">"{campaign.subject}"</p>

                        <div className="flex items-center flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                          <span>📊 {displaySent} enviados</span>
                          {displayFailed > 0 && <span className="text-red-500">✗ {displayFailed} falhou</span>}
                          {campaign.total_prospects > 0 && (
                            <span>👥 {campaign.total_prospects} total</span>
                          )}
                          <span className={campaign.open_rate > 0 ? 'text-emerald-600 font-medium' : ''}>
                            👀 {displaySent > 0 ? (campaign.open_rate > 0 ? `${campaign.open_rate}% abertos` : '0% abertos') : 'Sem dados'}
                          </span>
                          <span className={campaign.click_rate > 0 ? 'text-blue-600 font-medium' : ''}>
                            🔗 {displaySent > 0 ? (campaign.click_rate > 0 ? `${campaign.click_rate}% cliques` : '0% cliques') : 'Sem dados'}
                          </span>
                          <span className={campaign.reply_count > 0 ? 'text-purple-600 font-medium' : ''}>
                            💬 {displaySent > 0 ? (campaign.reply_count > 0 ? `${campaign.reply_count} resp. (${campaign.reply_rate}%)` : '0 respostas') : 'Sem dados'}
                          </span>
                          {campaign.is_legacy_tracking && (
                            <span title="Campanha enviada antes da deduplicação por prospecto. As contagens refletem totais brutos, não aberturas/cliques únicos." className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 cursor-help">
                              ⚠️ Legado
                            </span>
                          )}
                        </div>

                        {getFollowUpBadge(campaign) && (
                          <div className="mt-2">{getFollowUpBadge(campaign)}</div>
                        )}

                        {isSending && (
                          <CampaignProgressBar campaignId={campaign.id} campaign={campaign} />
                        )}
                      </div>

                      <div className="flex items-center space-x-2 ml-4 shrink-0">
                        {campaign.status === 'draft' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditModal(campaign)}
                              className="border-slate-300 text-slate-700 hover:bg-slate-50"
                            >
                              ✏️ Editar
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleLaunch(campaign.id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              ▶ Enviar
                            </Button>
                          </>
                        )}
                        {isSending && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancel(campaign.id)}
                            className="border-red-300 text-red-600 hover:bg-red-50"
                          >
                            ✕ Cancelar envio
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const next = isExpanded ? null : campaign.id;
                            setSelectedCampaign(next);
                            if (next && !sendStatus[campaign.id]) {
                              api.get(`/prospecting/campaigns/${campaign.id}/status`)
                                .then(r => setSendStatus(prev => ({ ...prev, [campaign.id]: r.data })))
                                .catch(() => {});
                            }
                          }}
                        >
                          {isExpanded ? 'Fechar' : 'Detalhes'}
                        </Button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-slate-100 px-4 pb-4 pt-3 bg-slate-50/50">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                            <p className="text-2xl font-bold text-slate-800">{displaySent}</p>
                            <p className="text-xs text-slate-500 mt-0.5">Enviados</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                            <p className="text-2xl font-bold text-red-500">{displayFailed}</p>
                            <p className="text-xs text-slate-500 mt-0.5">Falhou</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                            {displaySent > 0 ? (
                              <>
                                <p className="text-2xl font-bold text-emerald-600">
                                  {campaign.open_rate > 0 ? `${campaign.open_rate}%` : '0%'}
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  Taxa de Abertura{campaign.open_count > 0 ? ` (${campaign.open_count}${campaign.is_legacy_tracking ? ' bruto' : ''})` : ''}
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-sm font-medium text-slate-400 mt-1">Sem dados</p>
                                <p className="text-xs text-slate-400 mt-0.5">Taxa de Abertura</p>
                              </>
                            )}
                          </div>
                          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                            {displaySent > 0 ? (
                              <>
                                <p className="text-2xl font-bold text-blue-600">
                                  {campaign.click_rate > 0 ? `${campaign.click_rate}%` : '0%'}
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  Taxa de Cliques{campaign.click_count > 0 ? ` (${campaign.click_count}${campaign.is_legacy_tracking ? ' bruto' : ''})` : ''}
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-sm font-medium text-slate-400 mt-1">Sem dados</p>
                                <p className="text-xs text-slate-400 mt-0.5">Taxa de Cliques</p>
                              </>
                            )}
                          </div>
                          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                            {displaySent > 0 ? (
                              <>
                                <p className="text-2xl font-bold text-purple-600">
                                  {campaign.reply_rate > 0 ? `${campaign.reply_rate}%` : '0%'}
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  Respostas{campaign.reply_count > 0 ? ` (${campaign.reply_count})` : ''}
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-sm font-medium text-slate-400 mt-1">Sem dados</p>
                                <p className="text-xs text-slate-400 mt-0.5">Respostas</p>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Email preview panel */}
                        {(campaign.content || campaign.follow_up_content) && (() => {
                          const hasFollowUp = campaign.follow_up_enabled && campaign.follow_up_content;
                          const activeTab = previewTab[campaign.id] || 'inicial';
                          const previewSubject = activeTab === 'followup'
                            ? (campaign.follow_up_subject || campaign.subject)
                            : campaign.subject;
                          const previewBody = activeTab === 'followup'
                            ? campaign.follow_up_content
                            : campaign.content;
                          const isHtml = previewBody && /<[a-z][\s\S]*>/i.test(previewBody);
                          const iframeDoc = isHtml
                            ? `<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:sans-serif;font-size:14px;color:#1e293b;margin:12px 16px;word-break:break-word;}a{color:#2563eb;}img{max-width:100%;}</style></head><body>${previewBody}</body></html>`
                            : `<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:sans-serif;font-size:14px;color:#1e293b;margin:12px 16px;white-space:pre-wrap;word-break:break-word;}</style></head><body>${previewBody ? previewBody.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;') : ''}</body></html>`;

                          return (
                            <div className="mt-3 rounded-lg border border-slate-200 overflow-hidden bg-white">
                              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">📄 Pré-visualização do email</span>
                                {hasFollowUp && (
                                  <div className="flex rounded-md overflow-hidden border border-slate-200 text-xs">
                                    <button
                                      onClick={() => setPreviewTab(t => ({ ...t, [campaign.id]: 'inicial' }))}
                                      className={`px-3 py-1 font-medium transition-colors ${activeTab === 'inicial' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                                    >
                                      Inicial
                                    </button>
                                    <button
                                      onClick={() => setPreviewTab(t => ({ ...t, [campaign.id]: 'followup' }))}
                                      className={`px-3 py-1 font-medium transition-colors ${activeTab === 'followup' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                                    >
                                      Follow-up
                                    </button>
                                  </div>
                                )}
                              </div>
                              {previewSubject && (
                                <div className="px-3 py-2 bg-white border-b border-slate-100 text-xs text-slate-500">
                                  <span className="font-medium text-slate-700">Assunto:</span> {previewSubject}
                                </div>
                              )}
                              <iframe
                                key={`${campaign.id}-${activeTab}`}
                                srcDoc={iframeDoc}
                                sandbox="allow-same-origin"
                                title={`Pré-visualização ${activeTab}`}
                                className="w-full border-0"
                                style={{ height: '220px' }}
                                onLoad={e => {
                                  try {
                                    const doc = e.target.contentDocument;
                                    if (doc) {
                                      const h = doc.documentElement.scrollHeight;
                                      if (h > 0) e.target.style.height = Math.min(h + 4, 400) + 'px';
                                    }
                                  } catch (_) {}
                                }}
                              />
                            </div>
                          );
                        })()}

                        {(sendStatus[campaign.id]?.deliveries?.length > 0 || isSending) && (
                          <DeliveryTable campaignId={campaign.id} />
                        )}

                        {!sendStatus[campaign.id]?.deliveries?.length && !isSending && (
                          <p className="text-xs text-slate-400 text-center py-2">
                            {campaign.status === 'draft' ? 'Campanha ainda não enviada.' : 'Nenhum registro de entrega disponível.'}
                          </p>
                        )}

                        {campaign.status !== 'draft' && (campaign.open_count > 0 || campaign.click_count > 0) && (
                          <div className="mt-3 flex justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setTrackingCampaign(campaign)}
                              className="text-xs border-slate-300 text-slate-600 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
                            >
                              📊 Ver rastreamento por prospecto
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>✉️ Nova Campanha de Email</DialogTitle>
            <DialogDescription>Configure os detalhes da campanha e o conteúdo do email.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="camp-name">Nome da campanha *</Label>
                <Input
                  id="camp-name"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ex: Prospecção Outubro 2025"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="camp-sender-name">Nome do remetente *</Label>
                  <Input
                    id="camp-sender-name"
                    value={form.sender_name}
                    onChange={e => setForm(f => ({ ...f, sender_name: e.target.value }))}
                    placeholder="Ex: João Silva"
                  />
                </div>
                <div>
                  <Label htmlFor="camp-sender-email">Email do remetente *</Label>
                  <Input
                    id="camp-sender-email"
                    type="email"
                    value={form.sender_email}
                    onChange={e => setForm(f => ({ ...f, sender_email: e.target.value }))}
                    placeholder="voce@empresa.com"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="camp-list">Lista de prospectos *</Label>
                <Select value={form.prospect_list_id} onValueChange={v => setForm(f => ({ ...f, prospect_list_id: v }))}>
                  <SelectTrigger id="camp-list">
                    <SelectValue placeholder="Selecione uma lista..." />
                  </SelectTrigger>
                  <SelectContent>
                    {prospectLists.map(list => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name} ({list.prospect_count ?? 0} prospectos)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-3">
                <p className="text-sm font-semibold text-slate-700 mb-3">📧 Email inicial</p>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="camp-subject">Assunto *</Label>
                    <Input
                      id="camp-subject"
                      value={form.subject}
                      onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                      placeholder="Assunto do email inicial"
                    />
                  </div>
                  <div>
                    <Label htmlFor="camp-content">Corpo do email *</Label>
                    <Textarea
                      id="camp-content"
                      value={form.content}
                      onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                      placeholder="Conteúdo do email (suporta HTML)"
                      rows={5}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">🔄 Follow-up automático</p>
                    <p className="text-xs text-slate-500">Enviar um segundo email para quem não respondeu</p>
                  </div>
                  <Switch
                    checked={form.follow_up_enabled}
                    onCheckedChange={v => setForm(f => ({ ...f, follow_up_enabled: v }))}
                  />
                </div>

                {form.follow_up_enabled && (
                  <div className="space-y-3 pl-1">
                    <div>
                      <Label htmlFor="camp-fu-days">Dias após o envio inicial</Label>
                      <Input
                        id="camp-fu-days"
                        type="number"
                        min={1}
                        max={30}
                        value={form.follow_up_days}
                        onChange={e => setForm(f => ({ ...f, follow_up_days: parseInt(e.target.value) || 3 }))}
                        className="w-28 mt-1"
                      />
                    </div>

                    <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 space-y-3">
                      <p className="text-xs font-semibold text-blue-700">Conteúdo do follow-up (opcional)</p>
                      <p className="text-xs text-slate-500">
                        Deixe em branco para reutilizar o email inicial com "[Follow-up]" no assunto.
                      </p>
                      <div>
                        <Label htmlFor="camp-fu-subject">Assunto do follow-up</Label>
                        <Input
                          id="camp-fu-subject"
                          value={form.follow_up_subject}
                          onChange={e => setForm(f => ({ ...f, follow_up_subject: e.target.value }))}
                          placeholder={`[Follow-up] ${form.subject || 'assunto do email inicial'}`}
                        />
                      </div>
                      <div>
                        <Label htmlFor="camp-fu-content">Corpo do follow-up</Label>
                        <Textarea
                          id="camp-fu-content"
                          value={form.follow_up_content}
                          onChange={e => setForm(f => ({ ...f, follow_up_content: e.target.value }))}
                          placeholder="Escreva uma mensagem de segundo toque mais pessoal..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={creating}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white">
              {creating ? 'Criando…' : '✓ Criar campanha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>✏️ Editar Campanha</DialogTitle>
            <DialogDescription>Atualize os campos da campanha (disponível apenas em rascunho).</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="edit-camp-name">Nome da campanha *</Label>
                <Input
                  id="edit-camp-name"
                  value={editForm.name}
                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ex: Prospecção Outubro 2025"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit-camp-sender-name">Nome do remetente *</Label>
                  <Input
                    id="edit-camp-sender-name"
                    value={editForm.sender_name}
                    onChange={e => setEditForm(f => ({ ...f, sender_name: e.target.value }))}
                    placeholder="Ex: João Silva"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-camp-sender-email">Email do remetente *</Label>
                  <Input
                    id="edit-camp-sender-email"
                    type="email"
                    value={editForm.sender_email}
                    onChange={e => setEditForm(f => ({ ...f, sender_email: e.target.value }))}
                    placeholder="voce@empresa.com"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit-camp-list">Lista de prospectos *</Label>
                <Select value={editForm.prospect_list_id} onValueChange={v => setEditForm(f => ({ ...f, prospect_list_id: v }))}>
                  <SelectTrigger id="edit-camp-list">
                    <SelectValue placeholder="Selecione uma lista..." />
                  </SelectTrigger>
                  <SelectContent>
                    {prospectLists.map(list => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name} ({list.prospect_count ?? 0} prospectos)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-3">
                <p className="text-sm font-semibold text-slate-700 mb-3">📧 Email inicial</p>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="edit-camp-subject">Assunto *</Label>
                    <Input
                      id="edit-camp-subject"
                      value={editForm.subject}
                      onChange={e => setEditForm(f => ({ ...f, subject: e.target.value }))}
                      placeholder="Assunto do email inicial"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-camp-content">Corpo do email *</Label>
                    <Textarea
                      id="edit-camp-content"
                      value={editForm.content}
                      onChange={e => setEditForm(f => ({ ...f, content: e.target.value }))}
                      placeholder="Conteúdo do email (suporta HTML)"
                      rows={5}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">🔄 Follow-up automático</p>
                    <p className="text-xs text-slate-500">Enviar um segundo email para quem não respondeu</p>
                  </div>
                  <Switch
                    checked={editForm.follow_up_enabled}
                    onCheckedChange={v => setEditForm(f => ({ ...f, follow_up_enabled: v }))}
                  />
                </div>

                {editForm.follow_up_enabled && (
                  <div className="space-y-3 pl-1">
                    <div>
                      <Label htmlFor="edit-camp-fu-days">Dias após o envio inicial</Label>
                      <Input
                        id="edit-camp-fu-days"
                        type="number"
                        min={1}
                        max={30}
                        value={editForm.follow_up_days}
                        onChange={e => setEditForm(f => ({ ...f, follow_up_days: parseInt(e.target.value) || 3 }))}
                        className="w-28 mt-1"
                      />
                    </div>

                    <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 space-y-3">
                      <p className="text-xs font-semibold text-blue-700">Conteúdo do follow-up (opcional)</p>
                      <p className="text-xs text-slate-500">
                        Deixe em branco para reutilizar o email inicial com "[Follow-up]" no assunto.
                      </p>
                      <div>
                        <Label htmlFor="edit-camp-fu-subject">Assunto do follow-up</Label>
                        <Input
                          id="edit-camp-fu-subject"
                          value={editForm.follow_up_subject}
                          onChange={e => setEditForm(f => ({ ...f, follow_up_subject: e.target.value }))}
                          placeholder={`[Follow-up] ${editForm.subject || 'assunto do email inicial'}`}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-camp-fu-content">Corpo do follow-up</Label>
                        <Textarea
                          id="edit-camp-fu-content"
                          value={editForm.follow_up_content}
                          onChange={e => setEditForm(f => ({ ...f, follow_up_content: e.target.value }))}
                          placeholder="Escreva uma mensagem de segundo toque mais pessoal..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleUpdate} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? 'Salvando…' : '✓ Salvar alterações'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {trackingCampaign && (
        <TrackingModal
          campaign={trackingCampaign}
          onClose={() => setTrackingCampaign(null)}
          api={api}
        />
      )}
      </>
      )}
    </div>
  );
}
